import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, CalendarDays } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import DiscoverBanner from "../components/DiscoverBanner";
import { articles } from "../mock";

export function Blog() {
  const [featured, ...rest] = articles;
  return (
    <div className="overflow-hidden">
      <Seo
        title="Insights on Child Cognition & Learning | MapMyKid Blog"
        description="Read MapMyKid Insights: articles on child cognition, learning, focus, multiple intelligence and parenting. Understand how children think, learn and grow their true potential."
      />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-12">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>Insights</Eyebrow>
          <h1 className="mt-6 text-[28px] sm:text-[46px] font-extrabold leading-[1.08] text-slate-900">
            Ideas on how children <span className="brand-gradient-text">think, learn & grow.</span>
          </h1>
          <p className="mt-4 text-[18px] leading-relaxed text-slate-600">
            Practical, science-backed reads on cognition, focus, learning and parenting.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Featured */}
          <Reveal>
            <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-[28px] border border-violet-100 overflow-hidden card-hover">
              <div className="relative h-64 lg:h-80 overflow-hidden">
                <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                <span className="absolute left-4 top-4 rounded-full cta-gradient px-3 py-1 text-[13px] font-bold text-white">Featured</span>
              </div>
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 text-[14px] text-slate-500">
                  <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">{featured.category}</span>
                  <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {featured.readTime}</span>
                </div>
                <h2 className="mt-3 text-[26px] font-extrabold text-slate-900 leading-tight">{featured.title}</h2>
                <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[16px] font-semibold text-violet-700 group-hover:gap-2.5 transition-all">Read article <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          </Reveal>

          {/* Grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 90}>
                <Link to={`/blog/${a.slug}`} className="group flex flex-col h-full rounded-3xl border border-violet-100 overflow-hidden card-hover">
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-center gap-3 text-[14px] text-slate-500">
                      <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">{a.category}</span>
                      <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {a.readTime}</span>
                    </div>
                    <h3 className="mt-3 text-[19px] font-bold text-slate-900 leading-snug">{a.title}</h3>
                    <p className="mt-2 text-[16px] text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[16px] font-semibold text-violet-700 group-hover:gap-2.5 transition-all">Read more <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <DiscoverBanner />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = articles.find((a) => a.slug === slug);
  const more = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <div className="overflow-hidden">
        <Seo title="Article not found | MapMyKid Insights" description="The article you are looking for could not be found." />
        <section className="section-pad pt-40 text-center">
          <h1 className="text-[28px] font-extrabold text-slate-900">Article not found</h1>
          <Link to="/blog" className="mt-6 inline-block"><Button className="cta-gradient text-white rounded-full h-12 px-6 text-[16px]">Back to Insights</Button></Link>
        </section>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <Seo title={`${article.title} | MapMyKid Insights`} description={article.excerpt} />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-10">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 animate-fade-up">
          <button onClick={() => navigate("/blog")} className="mb-6 inline-flex items-center gap-1.5 text-[16px] font-semibold text-violet-700 hover:gap-2.5 transition-all">
            <ArrowLeft className="h-4 w-4" /> All Insights
          </button>
          <div className="flex items-center gap-3 text-[14px] text-slate-500">
            <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">{article.category}</span>
            <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {article.date}</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {article.readTime}</span>
          </div>
          <h1 className="mt-4 text-[28px] sm:text-[42px] font-extrabold leading-[1.1] text-slate-900">{article.title}</h1>
        </div>
      </section>

      <section className="pb-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-[28px] overflow-hidden ring-1 ring-violet-100 shadow-xl shadow-violet-100/50">
            <img src={article.image} alt={article.title} className="h-64 sm:h-96 w-full object-cover" />
          </div>
          <div className="mt-8 space-y-5">
            {article.body.map((p, i) => (
              <p key={i} className="text-[18px] leading-relaxed text-slate-700">{p}</p>
            ))}
          </div>

          <div className="mt-10 rounded-3xl cta-gradient p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-60" />
            <div className="relative">
              <h3 className="text-[24px] font-extrabold text-white">Curious about your own child?</h3>
              <p className="mt-2 text-[17px] text-white/85">Book a MapMyKid assessment and discover their natural abilities.</p>
              <Link to="/book" className="mt-6 inline-block"><Button className="bg-white text-violet-700 hover:bg-violet-50 rounded-full h-13 h-14 px-7 text-[16px] font-bold">Book an Assessment <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white pt-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Keep Reading" title="More insights" />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {more.map((a, i) => (
              <Reveal key={a.slug} delay={i * 90}>
                <Link to={`/blog/${a.slug}`} className="group flex flex-col h-full rounded-3xl border border-violet-100 overflow-hidden card-hover">
                  <div className="relative h-44 overflow-hidden">
                    <img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-[18px] font-bold text-slate-900 leading-snug">{a.title}</h3>
                    <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
